import { SubscriptionStatus, SubscriptionType, type Subscription } from "gitpod-next-api/gitpod/v1/billing_pb";
import { type PlainMessage } from "@bufbuild/protobuf";
import { CREDITS_TO_DOLLARS_RATIO } from "@/routes/settings/organization/billing/billing-constants";

export const formatCredits = (credits: number) => {
    return parseFloat(credits.toFixed(1));
};

export const creditsToDollars = (credits: number): number => {
    return credits / CREDITS_TO_DOLLARS_RATIO;
};

/**
 * Computes the effective subscription status for Core plans.
 *
 * Logic:
 * - Active if at least one Core plan is active
 * - Cancelled if all Core plans are cancelled/expired
 * - Returns the most recent active subscription if multiple exist
 * - Returns the most recent cancelled subscription if all are cancelled
 */
export const computeCoreSubscriptionStatus = (
    subscriptions: PlainMessage<Subscription>[],
): PlainMessage<Subscription> | undefined => {
    const coreSubscriptions = subscriptions.filter((s) => s.subscriptionType === SubscriptionType.CORE);

    if (coreSubscriptions.length === 0) {
        return undefined;
    }

    // Find active subscriptions first
    const activeSubscriptions = coreSubscriptions.filter((s) => s.status === SubscriptionStatus.ACTIVE);

    if (activeSubscriptions.length > 0) {
        // Return the most recent active subscription (by contract_id as proxy for recency)
        return activeSubscriptions.sort((a, b) => b.contractId.localeCompare(a.contractId))[0];
    }

    // If no active subscriptions, return the most recent cancelled/expired one
    const cancelledSubscriptions = coreSubscriptions.filter(
        (s) => s.status === SubscriptionStatus.CANCELLED || s.status === SubscriptionStatus.EXPIRED,
    );

    if (cancelledSubscriptions.length > 0) {
        return cancelledSubscriptions.sort((a, b) => b.contractId.localeCompare(a.contractId))[0];
    }

    // Fallback to the first subscription if no clear status
    return coreSubscriptions[0];
};
